import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const AttendanceReport2 = () => {
  const { t } = useTranslation();
  const { user: userStorage } = useAuth();
  const token = userStorage?.token;

  const MonthOptions = [
    { id: "", value: t("hr.allMonths") },
    { id: 1, value: "January" },
    { id: 2, value: "February" },
    { id: 3, value: "March" },
    { id: 4, value: "April" },
    { id: 5, value: "May" },
    { id: 6, value: "June" },
    { id: 7, value: "July" },
    { id: 8, value: "August" },
    { id: 9, value: "September" },
    { id: 10, value: "October" },
    { id: 11, value: "November" },
    { id: 12, value: "December" },
  ];

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const startYear = 2023;
  const Years = [
    { id: "", value: t("hr.allYears") },
    ...Array.from({ length: currentYear - startYear + 1 }, (_, index) => ({
      id: startYear + index,
      value: startYear + index,
    })),
  ];

  const [user, setUser] = useState("");
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [userAttendanceData, setUserAttendanceData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [totals, setTotals] = useState({ totalPresent: 0, totalAbsent: 0 });

  const { data: usersData, isLoading: isLoadingUsers } = useFetchObjects(
    ["attendance-users"],
    "attendance-users",
    token,
  );

  const {
    data: recordsData,
    refetch,
    isLoading: isLoadingRecords,
  } = useFetchObjects(
    ["attendance-records", user, year, month],
    `attendance-records/?user_id=${user}${year ? `&year=${year}` : ""}${
      month ? `&month=${month}` : ""
    }`,
    token,
  );

  useEffect(() => {
    if (usersData) {
      const formattedUsers = Object.entries(usersData).map(([key, value]) => ({
        user_id: key,
        name: value,
      }));
      setUserAttendanceData(formattedUsers);
    }
  }, [usersData]);

  useEffect(() => {
    if (recordsData) {
      const formattedData = recordsData.map((record, index) => ({
        number: index + 1,
        user: record.user_name,
        date: record.date,
        time: record.time,
        attendanceType: record.attendanceType,
        status: record.status,
      }));
      setAttendanceData(formattedData);
      setTotals(calculateDailyTotals(formattedData));
    }
  }, [recordsData]);

  const calculateDailyTotals = (data) => {
    let totalPresent = 0;
    let totalAbsent = 0;

    // Group records by date
    const groupedByDate = data.reduce((acc, record) => {
      if (!acc[record.date]) acc[record.date] = [];
      acc[record.date].push(record);
      return acc;
    }, {});

    Object.entries(groupedByDate).forEach(([date, records]) => {
      const hasCheckInPresent = records.some(
        (r) => r.attendanceType === "Check-in" && r.status === "Present",
      );
      const hasCheckOutPresent = records.some(
        (r) => r.attendanceType === "Check-out" && r.status === "Present",
      );
      const hasAbsent = records.some((r) => r.status === "Absent");

      if (hasAbsent) {
        totalAbsent++;
      } else if (hasCheckInPresent || hasCheckOutPresent) {
        totalPresent++;
      }
    });

    return { totalPresent, totalAbsent };
  };

  const handleFilter = () => {
    refetch();
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Title for the report
    const title = "Attendance Report";

    // Define the headers
    const attendanceHeaders = [
      ["#", "User", "Date", "Time", "Attendance Type", "Status"],
    ];

    // Prepare the attendance data
    const attendanceDataFormatted = attendanceData.map((record) => [
      record.number,
      record.user,
      record.date,
      record.time,
      record.attendanceType,
      record.status,
    ]);

    // Combine the title, headers, and data
    const data = [[title], ...attendanceHeaders, ...attendanceDataFormatted];

    // Add summary data at the bottom
    const summary = [
      [],
      ["", "", "", "Summary", `Total Present: ${totals.totalPresent}`, ""],
      ["", "", "", "", `Total Absent: ${totals.totalAbsent}`, ""],
    ];
    const combinedData = [...data, ...summary];

    // Create a worksheet
    const ws = XLSX.utils.aoa_to_sheet(combinedData);

    // Style the title row
    ws["A1"].s = {
      font: { bold: true, sz: 16, color: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // Merge the title row across all columns
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: attendanceHeaders[0].length - 1 } },
    ];

    // Style the header row
    attendanceHeaders[0].forEach((header, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 1, c: colIndex });
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4F81BD" } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    });

    // Add auto-width for all columns
    const colWidths = attendanceHeaders[0].map((header, i) => ({
      wch: Math.max(
        header.length,
        ...attendanceDataFormatted.map((row) =>
          row[i] ? row[i].toString().length : 0,
        ),
      ),
    }));
    ws["!cols"] = colWidths;

    // Apply borders to all cells
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) ws[cellAddress] = {}; // Ensure the cell exists
        ws[cellAddress].s = ws[cellAddress].s || {};
        ws[cellAddress].s.border = {
          top: { style: "thin", color: { rgb: "CCCCCC" } },
          bottom: { style: "thin", color: { rgb: "CCCCCC" } },
          left: { style: "thin", color: { rgb: "CCCCCC" } },
          right: { style: "thin", color: { rgb: "CCCCCC" } },
        };
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");

    XLSX.writeFile(wb, "Attendance_Report.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.autoTable({
      head: [["#", "User", "Date", "Time", "Attendance Type", "Status"]],
      body: attendanceData.map((record) => [
        record.number,
        record.user,
        record.date,
        record.time,
        record.attendanceType,
        record.status,
      ]),
      startY: 20,
    });

    doc.text(
      `Total Present: ${totals.totalPresent}`,
      14,
      doc.autoTable.previous.finalY + 10,
    );
    doc.text(
      `Total Absent: ${totals.totalAbsent}`,
      14,
      doc.autoTable.previous.finalY + 20,
    );

    doc.save("attendance.pdf");
  };

  return (
    <Container>
      <Paper sx={{ padding: "1rem", marginTop: "2rem" }}>
        <Typography variant="h4" color="primary" fontWeight={500}>
          {t("hr.attendanceReport")}
        </Typography>
        <Grid container spacing={3} sx={{ marginTop: "1rem" }}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.selectUser")}
              fullWidth
              value={user}
              onChange={(e) => setUser(e.target.value)}
            >
              {userAttendanceData.map((item) => (
                <MenuItem key={item.user_id} value={item.user_id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.year")}
              fullWidth
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {Years.map((yearOption) => (
                <MenuItem key={yearOption.id} value={yearOption.id}>
                  {yearOption.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.month")}
              fullWidth
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {MonthOptions.map((monthOption) => (
                <MenuItem key={monthOption.id} value={monthOption.id}>
                  {monthOption.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} textAlign="right">
            <Button variant="contained" color="primary" onClick={handleFilter}>
              {t("hr.filter")}
            </Button>
          </Grid>
        </Grid>

        {isLoadingUsers || isLoadingRecords ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Box display="flex" justifyContent="flex-end" mb={2} mt={2}>
              <Button
                onClick={exportToExcel}
                variant="contained"
                sx={{ mr: 2 }}
              >
                {t("hr.exportToExcel")}
              </Button>
              <Button
                onClick={exportToPDF}
                variant="contained"
                color="secondary"
              >
                {t("hr.exportToPDF")}
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t("hr.user")}</TableCell>
                    <TableCell>{t("hr.date")}</TableCell>
                    <TableCell>{t("hr.time")}</TableCell>
                    <TableCell>{t("hr.attendanceType")}</TableCell>
                    <TableCell>{t("hr.status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceData.map((row) => (
                    <TableRow key={row.number}>
                      <TableCell>{row.number}</TableCell>
                      <TableCell>{row.user}</TableCell>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.time}</TableCell>
                      <TableCell>{row.attendanceType}</TableCell>
                      <TableCell>{row.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AttendanceReport2;
